import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { useWindowSize } from "hooks/useWindowSize";
import { Chip } from "components/DesignSystem/Chips/Chips";
import { convertDisplayToOptions } from "components/DesignSystem/Combobox/utils/convertDisplayToOptions";
import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import { NumericInput } from "components/NumericInput/NumericInput";
import { Price } from "components/Price";
import { PriceInput } from "components/PriceInput/PriceInput";
import {
  AMOUNT_TYPE,
  CURRENCY_PREFIX,
  FILTER_KEY,
  FILTER_TYPE,
  LABEL,
} from "constants/customer";
import {
  REVENUE_DATA_SOURCE_OPTIONS,
  REVENUE_DATA_SOURCE_TYPES,
} from "constants/revenueDataSource";
import { Form, Formik } from "formik";
import { UpdateFilterFunction } from "hooks/useFilter";
import { ChangeEvent, useMemo } from "react";
import { debounce } from "utils/debouncing";

export type CustomerFilterType = {
  SOURCES: REVENUE_DATA_SOURCE_TYPES[];
  MIN_AMOUNT_PAID: string;
  MAX_AMOUNT_PAID: string;
  MIN_AMOUNT_DUE: string;
  MAX_AMOUNT_DUE: string;
  MIN_TOTAL_INVOICED: string;
  MAX_TOTAL_INVOICED: string;
  HAS_OVERDUE: boolean;
  NO_OUTSTANDING_DUE: boolean;
  NO_PAYMENTS: boolean;
};

const AMOUNT_FILTER_CONFIG = {
  [AMOUNT_TYPE.PAID]: {
    minKey: FILTER_KEY.MIN_AMOUNT_PAID,
    maxKey: FILTER_KEY.MAX_AMOUNT_PAID,
    label: LABEL.AMOUNT_PAID,
  },
  [AMOUNT_TYPE.DUE]: {
    minKey: FILTER_KEY.MIN_AMOUNT_DUE,
    maxKey: FILTER_KEY.MAX_AMOUNT_DUE,
    label: LABEL.AMOUNT_DUE,
  },
  [AMOUNT_TYPE.TOTAL]: {
    minKey: FILTER_KEY.MIN_TOTAL_INVOICED,
    maxKey: FILTER_KEY.MAX_TOTAL_INVOICED,
    label: LABEL.TOTAL_INVOICED,
  },
} as const;

const AmountFilter = ({
  filter,
  updateFilter,
  type,
}: {
  filter: CustomerFilterType;
  updateFilter: UpdateFilterFunction<CustomerFilterType>;
  type: "PAID" | "DUE" | "TOTAL";
}) => {
  const { minKey, maxKey, label } = AMOUNT_FILTER_CONFIG[type];

  const handleAmountChange = debounce((e: ChangeEvent<HTMLFormElement>) => {
    const { value, name } = e.target;
    const numericValue = value.replace(/[^\d.-]/g, "");
    updateFilter(name as keyof CustomerFilterType, numericValue);
  });

  return (
    <Formik
      initialValues={{
        [minKey]: filter[minKey] || "",
        [maxKey]: filter[maxKey] || "",
      }}
      onSubmit={() => {}}
    >
      <Form
        className="all:unset t-flex t-flex-col t-gap-4"
        onChange={handleAmountChange}
      >
        <PriceInput
          name={minKey}
          label={`${LABEL.MIN_PREFIX} ${label}`}
          placeholder="0.00"
          currencyCode={CURRENCY_PREFIX}
        />
        <PriceInput
          name={maxKey}
          label={`${LABEL.MAX_PREFIX} ${label}`}
          placeholder="0.00"
          currencyCode={CURRENCY_PREFIX}
        />
      </Form>
    </Formik>
  );
};

const SourceFilter = ({
  filter,
  updateFilter,
}: {
  filter: CustomerFilterType;
  updateFilter: UpdateFilterFunction<CustomerFilterType>;
}) => {
  const selectedSource = filter.SOURCES || [];

  return (
    <MultiSelectFilter
      options={convertDisplayToOptions(REVENUE_DATA_SOURCE_OPTIONS)}
      selected={selectedSource}
      onChange={(newValues) =>
        updateFilter(
          FILTER_KEY.SOURCES,
          newValues as REVENUE_DATA_SOURCE_TYPES[]
        )
      }
      isSearchable={false}
      isSelectAll={false}
    />
  );
};

const OthersFilter = ({
  filter,
  updateFilter,
}: {
  filter: CustomerFilterType;
  updateFilter: UpdateFilterFunction<CustomerFilterType>;
}) => {
  return (
    <div className="t-flex t-flex-col t-gap-3">
      <Checkbox
        label={<>{LABEL.OVERDUE_INVOICES}</>}
        name={FILTER_KEY.HAS_OVERDUE}
        checked={filter.HAS_OVERDUE}
        onChange={(e) => updateFilter(FILTER_KEY.HAS_OVERDUE, e.target.checked)}
      />
      <Checkbox
        label={<>{LABEL.NO_OUTSTANDING_DUE}</>}
        name={FILTER_KEY.NO_OUTSTANDING_DUE}
        checked={filter.NO_OUTSTANDING_DUE}
        onChange={(e) =>
          updateFilter(FILTER_KEY.NO_OUTSTANDING_DUE, e.target.checked)
        }
      />
      <Checkbox
        label={<>{LABEL.NO_PAYMENTS}</>}
        name={FILTER_KEY.NO_PAYMENTS}
        checked={filter.NO_PAYMENTS}
        onChange={(e) => updateFilter(FILTER_KEY.NO_PAYMENTS, e.target.checked)}
      />
    </div>
  );
};

export const CustomerFilter = ({
  filter,
  updateFilter,
}: {
  filter: CustomerFilterType;
  updateFilter: UpdateFilterFunction<CustomerFilterType>;
}) => {
  const appliedFilterCount = useMemo(
    () =>
      Object.keys(filter).reduce((acc, key) => {
        if (key === FILTER_KEY.SOURCES) {
          return acc + (Array.isArray(filter[key]) ? filter[key].length : 0);
        }
        if (
          key === FILTER_KEY.HAS_OVERDUE ||
          key === FILTER_KEY.NO_OUTSTANDING_DUE ||
          key === FILTER_KEY.NO_PAYMENTS
        ) {
          return acc + (filter[key as keyof CustomerFilterType] ? 1 : 0);
        }
        if (filter[key as keyof CustomerFilterType]) {
          return acc + 1;
        }
        return acc;
      }, 0),
    [filter]
  );

  const { isMobile } = useWindowSize();

  return (
    <Filter.Root
      defaultValue={FILTER_TYPE.SOURCES}
      hideDivider={isMobile}
      title={
        appliedFilterCount > 0 ? (
          <span className="t-text-text-30">({appliedFilterCount})</span>
        ) : null
      }
      capsule={
        <span className="t-contents sm:t-hidden">
          <Chip
            onClick={() => updateFilter(FILTER_KEY.HAS_OVERDUE, true)}
            onRemove={() => updateFilter(FILTER_KEY.HAS_OVERDUE, false)}
            isRemovable={true}
            isFixedFilter={true}
            isActive={filter.HAS_OVERDUE}
            filterType={FILTER_TYPE.OTHERS}
          >
            {LABEL.OVERDUE_INVOICES_CHIP}
          </Chip>
          {filter.SOURCES.map((value) => (
            <Chip
              key={value}
              onRemove={() =>
                updateFilter(
                  FILTER_KEY.SOURCES,
                  filter.SOURCES.filter((v) => v !== value)
                )
              }
              isActive
              filterType={FILTER_TYPE.SOURCES}
            >
              <span>
                {
                  REVENUE_DATA_SOURCE_OPTIONS[
                    value as keyof typeof REVENUE_DATA_SOURCE_OPTIONS
                  ]
                }
              </span>
            </Chip>
          ))}
          {filter.MIN_AMOUNT_PAID && filter.MIN_AMOUNT_PAID.length > 0 && (
            <Chip
              onRemove={() => updateFilter(FILTER_KEY.MIN_AMOUNT_PAID, "")}
              isRemovable={true}
              isActive
              filterType={FILTER_TYPE.AMOUNT_PAID}
            >
              {LABEL.MIN_PREFIX} {LABEL.AMOUNT_PAID} {CURRENCY_PREFIX}
              {filter.MIN_AMOUNT_PAID}
            </Chip>
          )}
          {filter.MAX_AMOUNT_PAID && filter.MAX_AMOUNT_PAID.length > 0 && (
            <Chip
              onRemove={() => updateFilter(FILTER_KEY.MAX_AMOUNT_PAID, "")}
              isRemovable={true}
              isActive
              filterType={FILTER_TYPE.AMOUNT_PAID}
            >
              {LABEL.MAX_PREFIX} {LABEL.AMOUNT_PAID} {CURRENCY_PREFIX}
              {filter.MAX_AMOUNT_PAID}
            </Chip>
          )}
          {filter.MIN_AMOUNT_DUE && filter.MIN_AMOUNT_DUE.length > 0 && (
            <Chip
              onRemove={() => updateFilter(FILTER_KEY.MIN_AMOUNT_DUE, "")}
              isRemovable={true}
              isActive
              filterType={FILTER_TYPE.AMOUNT_DUE}
            >
              {LABEL.MIN_PREFIX} {LABEL.AMOUNT_DUE} {CURRENCY_PREFIX}
              {filter.MIN_AMOUNT_DUE}
            </Chip>
          )}
          {filter.MAX_AMOUNT_DUE && filter.MAX_AMOUNT_DUE.length > 0 && (
            <Chip
              onRemove={() => updateFilter(FILTER_KEY.MAX_AMOUNT_DUE, "")}
              isRemovable={true}
              isActive
              filterType={FILTER_TYPE.AMOUNT_DUE}
            >
              {LABEL.MAX_PREFIX} {LABEL.AMOUNT_DUE} {CURRENCY_PREFIX}
              {filter.MAX_AMOUNT_DUE}
            </Chip>
          )}
          {filter.MIN_TOTAL_INVOICED &&
            filter.MIN_TOTAL_INVOICED.length > 0 && (
              <Chip
                onRemove={() => updateFilter(FILTER_KEY.MIN_TOTAL_INVOICED, "")}
                isRemovable={true}
                isActive
                filterType={FILTER_TYPE.TOTAL_INVOICED}
              >
                {LABEL.MIN_PREFIX} {LABEL.TOTAL_INVOICED} {CURRENCY_PREFIX}
                {filter.MIN_TOTAL_INVOICED}
              </Chip>
            )}
          {filter.MAX_TOTAL_INVOICED &&
            filter.MAX_TOTAL_INVOICED.length > 0 && (
              <Chip
                onRemove={() => updateFilter(FILTER_KEY.MAX_TOTAL_INVOICED, "")}
                isRemovable={true}
                isActive
                filterType={FILTER_TYPE.TOTAL_INVOICED}
              >
                {LABEL.MAX_PREFIX} {LABEL.TOTAL_INVOICED} {CURRENCY_PREFIX}
                {filter.MAX_TOTAL_INVOICED}
              </Chip>
            )}
          {filter.NO_OUTSTANDING_DUE && (
            <Chip
              onRemove={() =>
                updateFilter(FILTER_KEY.NO_OUTSTANDING_DUE, false)
              }
              isRemovable={true}
              isActive
              filterType={FILTER_TYPE.OTHERS}
            >
              {LABEL.NO_OUTSTANDING_DUE}
            </Chip>
          )}
          {filter.NO_PAYMENTS && (
            <Chip
              onRemove={() => updateFilter(FILTER_KEY.NO_PAYMENTS, false)}
              isRemovable={true}
              isActive
              filterType={FILTER_TYPE.OTHERS}
            >
              {LABEL.NO_PAYMENTS}
            </Chip>
          )}
        </span>
      }
    >
      <Filter.Portal>
        <Filter.List>
          <Filter.ListItem value={FILTER_TYPE.SOURCES}>
            {LABEL.SOURCES}
          </Filter.ListItem>
          <Filter.ListItem value={FILTER_TYPE.AMOUNT_DUE}>
            {LABEL.AMOUNT_DUE}
          </Filter.ListItem>
          <Filter.ListItem value={FILTER_TYPE.AMOUNT_PAID}>
            {LABEL.AMOUNT_PAID}
          </Filter.ListItem>
          <Filter.ListItem value={FILTER_TYPE.TOTAL_INVOICED}>
            {LABEL.TOTAL_INVOICED}
          </Filter.ListItem>
          <Filter.ListItem value={FILTER_TYPE.OTHERS}>
            {LABEL.OTHERS}
          </Filter.ListItem>
        </Filter.List>
        <Filter.Body value={FILTER_TYPE.SOURCES} block>
          <SourceFilter filter={filter} updateFilter={updateFilter} />
        </Filter.Body>
        <Filter.Body value={FILTER_TYPE.AMOUNT_PAID} block>
          <AmountFilter
            filter={filter}
            updateFilter={updateFilter}
            type={AMOUNT_TYPE.PAID}
          />
        </Filter.Body>
        <Filter.Body value={FILTER_TYPE.AMOUNT_DUE} block>
          <AmountFilter
            filter={filter}
            updateFilter={updateFilter}
            type={AMOUNT_TYPE.DUE}
          />
        </Filter.Body>
        <Filter.Body value={FILTER_TYPE.TOTAL_INVOICED} block>
          <AmountFilter
            filter={filter}
            updateFilter={updateFilter}
            type={AMOUNT_TYPE.TOTAL}
          />
        </Filter.Body>
        <Filter.Body value={FILTER_TYPE.OTHERS} block>
          <OthersFilter filter={filter} updateFilter={updateFilter} />
        </Filter.Body>
      </Filter.Portal>
    </Filter.Root>
  );
};
